import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Layout, ProtectedRoute } from '../components/layout';
import { Button, Input, Card, CardBody } from '../components/common';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { updateProfile, changePassword, uploadAvatar, removeAvatar } from '../api/auth';

export const Settings = () => {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [removingAvatar, setRemovingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors, isDirty: profileIsDirty },
  } = useForm({
    defaultValues: {
      displayName: user?.displayName || '',
      email: user?.email || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    watch,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm();

  const newPassword = watch('newPassword');

  // Check if user has local auth method (can change password)
  const hasLocalAuth = !user?.authMethods?.length || user?.authMethods?.some(m => m.provider === 'local');

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      await uploadAvatar(file);
      await refreshUser();
      toast.success('Profile picture updated!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    setRemovingAvatar(true);
    try {
      await removeAvatar();
      await refreshUser();
      toast.success('Profile picture removed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove profile picture');
    } finally {
      setRemovingAvatar(false);
    }
  };

  const onSubmitProfile = async (data) => {
    setSavingProfile(true);

    try {
      const updates = {};
      if (data.displayName !== user?.displayName) {
        updates.displayName = data.displayName;
      }
      if (data.email !== user?.email) {
        updates.email = data.email;
      }

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save.');
        setSavingProfile(false);
        return;
      }

      await updateProfile(updates);
      await refreshUser();
      toast.success('Profile updated successfully!');
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.details) {
        toast.error(errorData.details.map(d => d.message).join(', '));
      } else {
        toast.error(errorData?.error || 'Failed to update profile.');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const onSubmitPassword = async (data) => {
    setChangingPassword(true);

    try {
      await changePassword(data.currentPassword, data.newPassword);
      toast.success('Password changed successfully!');
      resetPassword();
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.details) {
        toast.error(errorData.details.map(d => d.message).join(', '));
      } else {
        toast.error(errorData?.error || 'Failed to change password.');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <ProtectedRoute>
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold mb-6">Settings</h1>

          {/* Profile Picture */}
          <Card className="mb-6">
            <CardBody>
              <h2 className="text-lg font-semibold mb-4">Profile Picture</h2>
              <div className="flex items-center gap-6">
                <div className="relative">
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.displayName}
                      className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center border-2 border-gray-200">
                      <span className="text-3xl font-bold text-primary-600">
                        {user?.displayName?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  {uploadingAvatar && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                      <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleAvatarClick}
                    disabled={uploadingAvatar}
                  >
                    {user?.avatarUrl ? 'Change Picture' : 'Upload Picture'}
                  </Button>
                  {user?.avatarUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveAvatar}
                      loading={removingAvatar}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remove Picture
                    </Button>
                  )}
                  <p className="text-xs text-gray-500">
                    JPEG, PNG, GIF, or WebP. Max 2MB.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Profile Settings */}
          <Card className="mb-6">
            <CardBody>
              <h2 className="text-lg font-semibold mb-4">Profile</h2>
              <form onSubmit={handleSubmitProfile(onSubmitProfile)} className="space-y-4">
                <Input
                  label="Display Name"
                  {...registerProfile('displayName', {
                    required: 'Display name is required',
                    minLength: {
                      value: 2,
                      message: 'Display name must be at least 2 characters',
                    },
                    maxLength: {
                      value: 30,
                      message: 'Display name cannot exceed 30 characters',
                    },
                  })}
                  error={profileErrors.displayName?.message}
                  placeholder="How others will see you"
                />

                <Input
                  label="Email"
                  type="email"
                  {...registerProfile('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={profileErrors.email?.message}
                  placeholder="you@example.com"
                />

                <Button
                  type="submit"
                  loading={savingProfile}
                  disabled={!profileIsDirty}
                >
                  Save Profile
                </Button>
              </form>
            </CardBody>
          </Card>

          {/* Account Info */}
          <Card className="mb-6">
            <CardBody>
              <h2 className="text-lg font-semibold mb-4">Account Information</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-500">Sign-in Methods</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {user?.authMethods?.length > 0 ? (
                      user.authMethods.map((method) => (
                        <span
                          key={method.provider}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 capitalize"
                        >
                          {method.provider}
                        </span>
                      ))
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        Local
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Change Password */}
          {hasLocalAuth && (
            <Card>
              <CardBody>
                <h2 className="text-lg font-semibold mb-4">Change Password</h2>
                <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-4">
                  <Input
                    label="Current Password"
                    type="password"
                    {...registerPassword('currentPassword', {
                      required: 'Current password is required',
                    })}
                    error={passwordErrors.currentPassword?.message}
                    placeholder="Enter current password"
                  />

                  <Input
                    label="New Password"
                    type="password"
                    {...registerPassword('newPassword', {
                      required: 'New password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                      validate: {
                        hasLowercase: (v) =>
                          /[a-z]/.test(v) || 'Must contain a lowercase letter',
                        hasUppercase: (v) =>
                          /[A-Z]/.test(v) || 'Must contain an uppercase letter',
                        hasNumber: (v) =>
                          /[0-9]/.test(v) || 'Must contain a number',
                      },
                    })}
                    error={passwordErrors.newPassword?.message}
                    placeholder="Min 8 chars, mixed case + number"
                  />

                  <Input
                    label="Confirm New Password"
                    type="password"
                    {...registerPassword('confirmPassword', {
                      required: 'Please confirm your new password',
                      validate: value => value === newPassword || 'Passwords do not match',
                    })}
                    error={passwordErrors.confirmPassword?.message}
                    placeholder="Confirm new password"
                  />

                  <Button
                    type="submit"
                    loading={changingPassword}
                  >
                    Change Password
                  </Button>
                </form>
              </CardBody>
            </Card>
          )}

          {!hasLocalAuth && (
            <Card>
              <CardBody>
                <h2 className="text-lg font-semibold mb-4">Password</h2>
                <p className="text-gray-600">
                  You signed up using a social login provider. Password management is not available for OAuth-only accounts.
                </p>
              </CardBody>
            </Card>
          )}
        </div>
      </Layout>
    </ProtectedRoute>
  );
};
